import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import CustomTable from '../../../shared/CustomTable'
import CustomPagination from '../../../shared/CustomPagination'
import CustomButton from '../../../shared/CustomButton'
import DatePicker from '../../../shared/DatePicker'
import TextInput from '../../../shared/TextInput'
import { formatDate } from '../../../utils/FormateDateTime'
import ReusableModal from '../../../shared/ReuseableModal'
import { MdBuildCircle } from 'react-icons/md'
import PolicyHolderFixModal from './PolicyHolderFixModal'
import { useGetBulkJobFailuresQuery } from '../../../redux/api/modules/claimsApi'

function BulkJobFailuresTab({ filters, setFilters }) {
  const { t } = useTranslation()
  const [showPolicyHolderFixModal, setShowPolicyHolderFixModal] = useState(false)
  const [appliedFilters, setAppliedFilters] = useState(filters)

  const {
    data: response,
    isLoading: queryLoading,
    isFetching: queryFetching,
    refetch
  } = useGetBulkJobFailuresQuery(appliedFilters)

  const loading = queryLoading || queryFetching
  const data = response?.data || []
  const pagination = {
    page: response?.page || appliedFilters?.page || 1,
    per_page: response?.per_page || appliedFilters?.per_page || 20,
    total_records: response?.total_records || 0,
    total_pages: response?.total_pages || 0
  }

  const updateFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }))
  }

  const handleApplyFilters = () => {
    const next = { ...filters, page: 1 }
    setFilters(next)
    if (JSON.stringify(next) === JSON.stringify(appliedFilters)) refetch()
    else setAppliedFilters(next)
  }

  const handleResetFilters = () => {
    const initialFilters = {
      start_date: moment().format('YYYY-MM-DD'),
      end_date: moment().format('YYYY-MM-DD'),
      file_number: '',
      patient_name: '',
      status: '',
      job_type: 'submit',
      page: 1,
      per_page: 20
    }
    setFilters(initialFilters)
    setAppliedFilters(initialFilters)
  }

  const handlePageChange = (page, perPage) => {
    const next = {
      ...appliedFilters,
      page,
      per_page: perPage
    }
    setFilters(prev => ({
      ...prev,
      page,
      per_page: perPage
    }))
    setAppliedFilters(next)
  }

  const columns = [
    {
      header: t('Visit Date'),
      body: row => row?.visit_date ? formatDate(row.visit_date) : '-'
    },
    {
      header: t('Patient Name'),
      body: row => row?.patient_name || '-'
    },
    {
      header: t('MRN'),
      body: row => row?.file_number || '-'
    },
    {
      header: t('Payer'),
      body: row => row?.payer_name || '-',
      bodyCellClass: '!whitespace-normal !min-w-[180px]'
    },
    {
      header: t('Error'),
      body: row => (
        <div className='max-w-[700px] whitespace-normal break-words text-red-600'>
          {row?.error_message || '-'}
        </div>
      )
    }
  ]

  return (
    <div className='space-y-3'>
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[repeat(4,minmax(0,1fr))_auto] gap-2 items-end'>
        <DatePicker
          useFormik={false}
          label={t('Visit Start Date')}
          value={filters?.start_date ? moment(filters.start_date, 'YYYY-MM-DD').toDate() : null}
          handleChange={e =>
            updateFilter('start_date', e?.value ? moment(e.value).format('YYYY-MM-DD') : '')
          }
          dateFormat='dd/mm/yy'
        />
        <DatePicker
          useFormik={false}
          label={t('Visit End Date')}
          value={filters?.end_date ? moment(filters.end_date, 'YYYY-MM-DD').toDate() : null}
          handleChange={e =>
            updateFilter('end_date', e?.value ? moment(e.value).format('YYYY-MM-DD') : '')
          }
          dateFormat='dd/mm/yy'
        />
        <TextInput
          useFormik={false}
          label={t('MRN')}
          placeholder={t('File Number')}
          value={filters?.file_number || ''}
          handleChange={e => updateFilter('file_number', e.target.value)}
        />
        <TextInput
          useFormik={false}
          label={t('Patient Name')}
          placeholder={t('Patient Name')}
          value={filters?.patient_name || ''}
          handleChange={e => updateFilter('patient_name', e.target.value)}
        />
        <div className='flex gap-2 justify-end md:col-span-2 lg:col-span-1 lg:justify-start mb-1'>
          <CustomButton
            label=''
            icon='pi pi-refresh'
            buttonType='secondary'
            className='!w-[35px] h-[35px] !px-0'
            onClick={handleResetFilters}
            disabled={loading}
            tooltip={t('Reset')}
            tooltipOptions={{ position: 'top' }}
          />
          <CustomButton
            label=''
            icon='pi pi-search'
            buttonType='primary'
            className='!w-[35px] h-[35px] !px-0'
            onClick={handleApplyFilters}
            loading={loading}
            showLoadingText={false}
            tooltip={t('Apply')}
            tooltipOptions={{ position: 'top' }}
          />
          <CustomButton
            label={t('Policy Holder Fix')}
            icon='pi pi-wrench'
            buttonType='success'
            className='w-fit h-[35px] whitespace-nowrap'
            onClick={() => setShowPolicyHolderFixModal(true)}
            disabled={loading}
          />
        </div>
      </div>

      <CustomTable
        data={data}
        isLoading={loading}
        columns={columns}
        emptyMessage={t('No job errors found')}
      />

      {pagination.total_records > 0 && (
        <CustomPagination
          currentPage={pagination.page}
          rowsPerPage={pagination.per_page}
          totalRecords={pagination.total_records}
          totalPages={pagination.total_pages}
          onPageChange={handlePageChange}
        />
      )}

      <ReusableModal
        isVisible={showPolicyHolderFixModal}
        onClose={() => setShowPolicyHolderFixModal(false)}
        title={t('Policy Holder Fix')}
        HeaderIcon={MdBuildCircle}
        className='w-full md:w-[620px] lg:w-[760px]'
        position='top'
      >
        <PolicyHolderFixModal
          filters={filters}
          onClose={() => setShowPolicyHolderFixModal(false)}
          onComplete={() => refetch()}
        />
      </ReusableModal>
    </div>
  )
}

export default BulkJobFailuresTab
